import type { ActoDetalle, Movimiento } from "@/lib/types";

export type ActoCategory =
  | "gravamen"
  | "cancelacion"
  | "aviso"
  | "compraventa"
  | "aclaratoria"
  | "otro";

export type ActoTone = "rose" | "emerald" | "amber" | "indigo" | "slate";

const CATEGORY_BY_CLAVE: Record<string, ActoCategory> = {
  I27: "gravamen",
  I21: "cancelacion",
  I34: "otro",
  I33: "aviso",
  I53: "aviso",
  I66: "compraventa",
  I15: "aclaratoria",
  I48: "otro",
};

const TONE_BY_CATEGORY: Record<ActoCategory, ActoTone> = {
  gravamen: "emerald",
  cancelacion: "rose",
  aviso: "amber",
  compraventa: "indigo",
  aclaratoria: "slate",
  otro: "slate",
};

export function categorize(m: Movimiento): ActoCategory {
  return CATEGORY_BY_CLAVE[m.clave] ?? "otro";
}

export function toneFor(category: ActoCategory): ActoTone {
  return TONE_BY_CATEGORY[category];
}

// Parses "DD-MM-YYYY" or "DD/MM/YYYY" to a sortable timestamp.
function parseFecha(s: string): number {
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (!m) return 0;
  const [, dd, mm, yyyy] = m;
  return Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd));
}

export function formatFecha(s: string): string {
  const ts = parseFecha(s);
  if (!ts) return s;
  return new Date(ts).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function getYear(s: string): string {
  const m = s.match(/^\d{2}[-/]\d{2}[-/](\d{4})$/);
  return m ? m[1] : "—";
}

export interface SortedMovimiento extends Movimiento {
  _sortKey: number;
}

// Most-recent first. Same date → higher consecutivo first (later in the day).
export function sortMovimientos(movs: Movimiento[]): SortedMovimiento[] {
  return movs
    .map<SortedMovimiento>((m) => ({
      ...m,
      _sortKey:
        parseFecha(m.fechaRegistro) * 100 + (Number(m.consecutivo) || 0),
    }))
    .sort((a, b) => b._sortKey - a._sortKey);
}

export interface FolioStatus {
  totalMovimientos: number;
  gravamenActivo: boolean;
  ultimoMovimientoFecha: string | null;
  ultimoMovimientoActo: string | null;
  conteoPorCategoria: Record<ActoCategory, number>;
  cancelacionesGravamen: number;
  avisosVigentes: number;
  ultimoGravamen: Movimiento | null;
}

const VIGENCIA_AVISO_DIAS = 60;

function diasDesde(fecha: string): number {
  const ts = parseFecha(fecha);
  if (!ts) return Infinity;
  return Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
}

export function analyzeFolio(movs: Movimiento[]): FolioStatus {
  const sorted = sortMovimientos(movs);
  const last = sorted[0];

  // Most recent event involving a gravamen-like clave.
  // I27 / I66 register a gravamen; I21 cancels one.
  const lastGravamen = sorted.find(
    (m) => m.clave === "I27" || m.clave === "I66",
  );
  const lastCancelacion = sorted.find((m) => m.clave === "I21");

  const gravamenActivo =
    !!lastGravamen &&
    (!lastCancelacion || lastGravamen._sortKey > lastCancelacion._sortKey);

  const conteoPorCategoria: Record<ActoCategory, number> = {
    gravamen: 0,
    cancelacion: 0,
    aviso: 0,
    compraventa: 0,
    aclaratoria: 0,
    otro: 0,
  };
  for (const m of movs) {
    conteoPorCategoria[categorize(m)]++;
  }
  // I66 (Compraventa con hipoteca) categoriza como "compraventa" para el tono
  // del timeline, pero conceptualmente también registra un gravamen — el chip
  // "Gravamen activo" ya lo trata así. Sumarlo al conteo para que el stat de
  // gravámenes refleje la realidad.
  conteoPorCategoria.gravamen += movs.filter((m) => m.clave === "I66").length;

  const avisosVigentes = movs.filter(
    (m) =>
      (m.clave === "I33" || m.clave === "I53") &&
      diasDesde(m.fechaRegistro) <= VIGENCIA_AVISO_DIAS,
  ).length;

  const cancelacionesGravamen = movs.filter((m) => m.clave === "I21").length;

  return {
    totalMovimientos: movs.length,
    gravamenActivo,
    ultimoMovimientoFecha: last?.fechaRegistro ?? null,
    ultimoMovimientoActo: last?.acto ?? null,
    conteoPorCategoria,
    cancelacionesGravamen,
    avisosVigentes,
    ultimoGravamen: lastGravamen ?? null,
  };
}

export interface CreditoInfo {
  tipoContrato: string | null;
  monto: string;
  moneda: string;
  plazo: string | null;
  tasa: string | null;
  acreditados: string;
  acreditante: string;
  garantia: string | null;
  fechaOtorgamiento: string | null;
}

const MONEDA_ALIAS: Record<string, string> = {
  "MONEDA NACIONAL": "MXN",
  "PESOS MEXICANOS": "MXN",
  "DOLARES": "USD",
};

function normKey(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .trim();
}

function findCampo(
  campos: Array<[string, string | null]>,
  key: string,
): string | null {
  const target = normKey(key);
  return campos.find(([k]) => normKey(k) === target)?.[1] ?? null;
}

function findAcross(
  acto: ActoDetalle,
  key: string,
): string | null {
  const main = findCampo(acto.campos, key);
  if (main != null) return main;
  for (const sub of acto.subActos ?? []) {
    const v = findCampo(sub.campos, key);
    if (v != null) return v;
  }
  return null;
}

function findAny(acto: ActoDetalle, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = findAcross(acto, k);
    if (v != null) return v;
  }
  return null;
}

export function extractCredito(acto: ActoDetalle | null): CreditoInfo | null {
  if (!acto) return null;
  // I27 (Gravamen): campos planos "HASTA POR LA CANTIDAD DE" + "ACREDITADOS"
  // en el tab principal o en sub-tab "Crédito".
  return extractGravamenStyle(acto) ?? extractCompraventaStyle(acto);
}

// Cubre tanto I27 (Gravamen, campos planos en el tab principal) como I66 sin
// cofinanciamiento (los mismos campos viven en la sub-clave HIPOTECA, que el
// mapper del Lambda ahora expone como sub-tab). findAcross recorre main +
// subActos así que ambos shapes funcionan sin distinción.
function extractGravamenStyle(acto: ActoDetalle): CreditoInfo | null {
  const monto = findAcross(acto, "HASTA POR LA CANTIDAD DE");
  const acreditados = findAcross(acto, "ACREDITADOS");
  if (!monto || !acreditados) return null;

  const monedaRaw = findAcross(acto, "EN") ?? "";
  const moneda = MONEDA_ALIAS[monedaRaw.toUpperCase()] ?? monedaRaw;

  // I27 usa "PLAZO DE"; HIPOTECA usa "PLAZO" pelado.
  const plazo = findAny(acto, "PLAZO DE", "PLAZO");
  const interes = findAcross(acto, "CON INTERÉS DEL");
  const periodo = findAcross(acto, "%");
  const tasa = interes
    ? `${interes}% ${periodo ?? ""}`.trim()
    : null;

  return {
    tipoContrato: findAcross(acto, "TIPO DE CONTRATO") ?? acto.nombre,
    monto,
    moneda,
    plazo,
    tasa,
    acreditados,
    // I27 usa "ACREDITANTE(S)" con paréntesis; HIPOTECA usa "ACREDITANTES".
    acreditante: findAny(acto, "ACREDITANTE(S)", "ACREDITANTES") ?? "—",
    garantia: findAcross(acto, "GARANTÍA HIPOTECARIA EN"),
    fechaOtorgamiento: findAcross(acto, "DE FECHA"),
  };
}

// I66 (Compraventa con hipoteca). Dos sub-shapes:
//
//   A) CON cofinanciamiento: sub-tab "Cofinanciamiento" con N grupos
//      consecutivos (cada uno empieza con un row ACREEDOR). Tomamos el de
//      monto más alto como primario y anotamos cuántos otros hay.
//
//   B) SIN cofinanciamiento (GRAVAMEN: SI, COFINANCIAMIENTO: NO): la
//      hipoteca es un crédito único embedido en el main tab. EN LA CANTIDAD
//      DE es el monto; GENERALES DEL ACREDITANTE el lender; GEN. COMPRADOR
//      Y/O ACREDITADO el deudor. No hay PLAZO ni tasa estructurados.
function extractCompraventaStyle(acto: ActoDetalle): CreditoInfo | null {
  const cof = acto.subActos?.find(
    (s) =>
      /cofinanc/i.test(s.titulo) ||
      s.campos.some(([k]) => normKey(k) === "ACREEDOR"),
  );
  if (cof) return fromCofinanciamiento(acto, cof);
  return fromHipotecaUnica(acto);
}

function fromCofinanciamiento(
  acto: ActoDetalle,
  cof: { campos: Array<[string, string | null]> },
): CreditoInfo | null {
  const groups: Array<Record<string, string>> = [];
  let current: Record<string, string> | null = null;
  for (const [k, v] of cof.campos) {
    const nk = normKey(k);
    if (nk === "ACREEDOR") {
      if (current) groups.push(current);
      current = {};
    }
    if (current && v) current[nk] = v;
  }
  if (current && Object.keys(current).length > 0) groups.push(current);
  if (groups.length === 0) return null;

  const parseMonto = (g: Record<string, string>) =>
    parseFloat((g["MONTO"] ?? "0").replace(/[,$\s]/g, "")) || 0;

  const primary = groups.reduce(
    (max, g) => (parseMonto(g) > parseMonto(max) ? g : max),
    groups[0],
  );

  const interes = primary["INTERES"];
  const tipoInt = primary["TIPO DE INTERES"];
  const tasa = interes ? `${interes}% ${tipoInt ?? ""}`.trim() : null;

  const monedaRaw = primary["MONEDA"] ?? "";
  const moneda = MONEDA_ALIAS[monedaRaw.toUpperCase()] ?? monedaRaw;

  const acreditados =
    findAcross(acto, "CRÉDITOS OTORGADOS A") ??
    findAcross(acto, "GEN. COMPRADOR Y/O ACREDITADO") ??
    "—";

  const monto = primary["MONTO"];
  if (!monto) return null;

  return {
    tipoContrato: acto.nombre,
    monto,
    moneda,
    plazo: primary["PLAZO"] ?? null,
    tasa,
    acreditados,
    acreditante:
      primary["ACREEDOR"] +
      (groups.length > 1 ? ` (+${groups.length - 1} cofinanciamiento)` : ""),
    garantia: primary["GRADO Y/O LUGAR"] ?? null,
    fechaOtorgamiento: findAcross(acto, "DE FECHA"),
  };
}

function fromHipotecaUnica(acto: ActoDetalle): CreditoInfo | null {
  // Sólo aplica si el folio reconoce explícitamente que hay gravamen y hay
  // un monto. Si GRAVAMEN no es "SI" la compraventa fue al contado.
  const gravamen = findAcross(acto, "GRAVAMEN");
  const monto = findAcross(acto, "EN LA CANTIDAD DE");
  if (gravamen?.toUpperCase() !== "SI" || !monto) return null;

  return {
    tipoContrato: acto.nombre,
    monto,
    moneda: "MXN", // no hay campo de moneda en este shape; default MX
    plazo: findAcross(acto, "PLAZO"),
    tasa: null, // no estructurado en este shape
    acreditados: findAcross(acto, "GEN. COMPRADOR Y/O ACREDITADO") ?? "—",
    acreditante: findAcross(acto, "GENERALES DEL ACREDITANTE") ?? "—",
    garantia: null,
    fechaOtorgamiento: findAcross(acto, "DE FECHA"),
  };
}

export function formatMonto(monto: string, moneda: string): string {
  // monto en formato "1,875,000.00" — anteponer signo y sufijar moneda
  return `$${monto} ${moneda}`.trim();
}

export function formatPlazo(plazo: string | null): string {
  if (!plazo) return "—";
  // "229 MESES" → "229 meses (~19 años)"
  const m = plazo.match(/(\d+)\s*MESES/i);
  if (m) {
    const meses = Number(m[1]);
    const anos = Math.floor(meses / 12);
    return `${meses} meses · ~${anos} años`;
  }
  return plazo;
}
